import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import { CheckCircle2, Store, User, ShoppingCart, Star, Clock, Gift, FileCheck, MapPin } from "lucide-react";
import type { UserRole } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import { CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import FixFloristRole from '@/components/auth/FixFloristRole';
import { loadGoogleMapsApi, isGoogleMapsLoaded } from "@/lib/google-maps-loader";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, supabase } = useAuth();
  const { toast } = useToast();
  
  // Get tab from URL parameter or default to "login"
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    tabParam === "florist" ? "florist" : 
    tabParam === "customer" ? "customer" : "login"
  );
  
  // State for error message
  const [error, setError] = useState<string | null>(null);
  
  // Login state
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Customer signup state
  const [customerSignupLoading, setCustomerSignupLoading] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    agreeToTerms: false,
    street_number: "",
    street_name: "",
    unit_number: "",
    suburb: "",
    state: "",
    postcode: "",
    phone: "",
  });
  
  // Florist signup state
  const [floristSignupLoading, setFloristSignupLoading] = useState(false);
  const [floristFormData, setFloristFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    storeName: "",
    phone: "",
    agreeToTerms: false
  });

  // Google Maps state
  const [isGoogleMapsLoadedState, setIsGoogleMapsLoadedState] = useState(false);
  const searchBoxRef = useRef<google.maps.places.StandaloneSearchBox | null>(null);

  // Load Google Maps API immediately when component mounts
  useEffect(() => {
    // Check if already loaded
    if (isGoogleMapsLoaded()) {
      setIsGoogleMapsLoadedState(true);
      return;
    }

    // Use centralized loader to prevent duplicates
    loadGoogleMapsApi(true)
      .then(() => {
        setIsGoogleMapsLoadedState(true);
        console.log('Google Maps API loaded successfully');
      })
      .catch((error) => {
        console.error('Failed to load Google Maps API:', error);
      });
  }, []);

  const onLoadSearchBox = (inputElement: HTMLInputElement) => {
    if (isGoogleMapsLoaded() && window.google && window.google.maps && window.google.maps.places) {
      const searchBox = new window.google.maps.places.Autocomplete(inputElement, {
        types: ['address'],
        componentRestrictions: { country: 'au' }, // Restrict to Australia
      });
      
      searchBox.addListener('place_changed', () => {
        const place = searchBox.getPlace();
        if (place && place.address_components) {
          let streetNumber = "";
          let route = "";
          let unitNumber = customerFormData.unit_number;
          let suburb = customerFormData.suburb;
          let state = customerFormData.state;
          let postalCode = customerFormData.postcode;

          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes("street_number")) streetNumber = component.long_name;
            if (types.includes("route")) route = component.long_name;
            if (types.includes("subpremise")) unitNumber = component.long_name;
            if (types.includes("locality")) suburb = component.long_name;
            if (!suburb && types.includes("postal_town")) suburb = component.long_name;
            if (!suburb && types.includes("sublocality_level_1")) suburb = component.long_name;
            if (!suburb && types.includes("neighborhood")) suburb = component.long_name;
            if (types.includes("administrative_area_level_1")) state = component.short_name;
            if (types.includes("postal_code")) postalCode = component.long_name;
          });

          setCustomerFormData(prev => ({
            ...prev,
            street_number: streetNumber,
            street_name: route,
            unit_number: unitNumber,
            suburb: suburb,
            state: state,
            postcode: postalCode,
          }));
        }
      });
    }
  };

  const onPlacesChanged = () => {
    // This function is no longer needed with the new approach
  };

  // Update URL when tab changes
  useEffect(() => {
    // This effect can be removed as it's for URL syncing, not critical
  }, [activeTab]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          if (error.message.includes("Invalid login credentials")) {
            return "Invalid email or password. Please check your credentials and try again.";
          }
          break;
        case 422:
          return "Invalid email format. Please enter a valid email address.";
        case 429:
          return "Too many login attempts. Please try again later.";
      }
    }
    return "An error occurred. Please try again.";
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!loginEmail || !loginPassword) {
      setError("Please fill in all required fields");
      return;
    }
    
    setLoginLoading(true);
    try {
      await signIn(loginEmail, loginPassword);
      navigate('/');
    } catch (error: any) {
      console.error("Login error:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle social login
  const handleSocialLogin = async (provider: 'google') => {
    setError(null);
    setLoginLoading(true);
    try {
      if (!supabase) {
        throw new Error("Supabase client not available for social login.");
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          data: { // Attempt to set user_metadata for new users
            role: 'customer',
            // Supabase should pick up name from Google, but you could set a default if needed
            // full_name: 'New Google User'
          },
          // You can add redirectTo if you want to send users to a specific page after login
          // redirectTo: window.location.origin + '/welcome', 
        },
      });
      if (error) {
        console.error(`${provider} login error:`, error);
        setError(`Failed to sign in with ${provider}: ${error.message}`);
        toast({
          title: `Login Error`, 
          description: `Could not sign in with ${provider}: ${error.message}`,
          variant: "destructive",
        });
      }
      // If successful, Supabase handles the redirect. No explicit navigation here usually.
    } catch (error: any) {
      console.error("Social login exception:", error);
      setError(error.message || `An unexpected error occurred with ${provider} login.`);
      toast({
        title: "Login Error",
        description: error.message || `An unexpected error occurred with ${provider} login.`,
        variant: "destructive",
      });
    } finally {
      // setLoginLoading(false); // Supabase redirects, so this might not always be hit if successful
      // Consider if setLoginLoading(false) should be here or if page will navigate away
    }
  };

  const handleCustomerSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    const { email, password, fullName, agreeToTerms, 
            street_number, street_name, unit_number, suburb, state, postcode, phone } = customerFormData;
    
    if (!email || !password || !fullName) {
      setError("Please fill in Name, Email, and Password.");
      return;
    }
    if (!street_name || !suburb || !state || !postcode || !phone || !street_number) {
      setError("Please fill in all required address and phone fields.");
      return;
    }
    if (!agreeToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy");
      return;
    }
    
    setCustomerSignupLoading(true);
    try {
      const { error: signUpError } = await signUp(email, password, fullName, "customer", {
        street_number: street_number,
        street_name: street_name,
        unit_number: unit_number,
        suburb: suburb,
        state: state,
        postcode: postcode,
        phone: phone,
      });

      if (signUpError) throw signUpError;
      
      toast({
        title: "Account created",
        description: "Please check your email for verification.",
      });
      
      setCustomerFormData({
        email: "",
        password: "",
        fullName: "",
        agreeToTerms: false,
        street_number: "",
        street_name: "",
        unit_number: "",
        suburb: "",
        state: "",
        postcode: "",
        phone: "",
      });
      
      setActiveTab("login");
    } catch (error: any) {
      setError(error.message || getErrorMessage(error as AuthError));
    } finally {
      setCustomerSignupLoading(false);
    }
  };

  const handleFloristSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    const { email, password, fullName, storeName, agreeToTerms } = floristFormData;
    
    if (!email || !password || !fullName || !storeName) {
      setError("Please fill in all required fields");
      return;
    }
    
    if (!agreeToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy");
      return;
    }
    
    setFloristSignupLoading(true);
    try {
      // signUp function handles authentication + store profile creation + auto-login
      const { error: signUpError } = await signUp(email, password, fullName, "florist", {
        store_name: storeName,
      });

      if (signUpError) {
        throw signUpError;
      }

      // No need to send email here - it's handled in AuthContext
      
      toast({
        title: "Welcome to MarketBloom!",
        description: "Your florist account has been created successfully. Check your email for next steps!",
      });
      
      // Reset form
      setFloristFormData({
        email: "",
        password: "",
        fullName: "",
        storeName: "",
        phone: "",
        agreeToTerms: false
      });
      
      // Redirect to florist dashboard
      navigate('/florist-dashboard');
    } catch (error: any) {
      console.error("Florist signup error:", error);
      setError(getErrorMessage(error));
    } finally {
      setFloristSignupLoading(false);
    }
  };

  // UI Components
  const TabIndicator = ({ activeTabIndex }: { activeTabIndex: number }) => {
    return (
      <div
        className="absolute bottom-0 h-0.5 bg-[#967F81] transition-all duration-300"
        style={{
          left: `${(activeTabIndex * 100) / 3}%`,
          width: `${100 / 3}%`,
        }}
      />
    );
  };

  const getTabClass = (isActive: boolean) => {
    return cn(
      "relative flex flex-col items-center justify-center p-4 cursor-pointer transition-all",
      {
        "text-brand-primary font-semibold": isActive,
        "text-gray-500 hover:text-brand-primary": !isActive,
      }
    );
  };

  // Convert tab name to index for positioning the indicator
  const tabToIndex = (tab: string): number => {
    switch (tab) {
      case "login": return 0;
      case "customer": return 1;
      case "florist": return 2;
      default: return 0;
    }
  };

  return (
    <>
      <FixFloristRole />
      <div className="min-h-screen bg-brand-accent flex items-center justify-center p-4 pt-24 relative z-10">
        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg overflow-hidden grid md:grid-cols-3">
          {/* Left panel - image and branding (hidden on mobile) */}
          <div className="hidden md:block bg-brand-muted p-8 flex flex-col justify-between">
            <div>
              <h1 className="text-3xl font-bold text-brand-primary">Market Bloom</h1>
              <p className="mt-4 text-gray-600">
                Australia's first online marketplace connecting flower lovers with local florists.
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-primary mt-0.5">
                    <CheckCircle size={20} />
                  </div>
                  <p className="ml-2 text-sm text-gray-600">
                    Support local florists in your area
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-primary mt-0.5">
                    <CheckCircle size={20} />
                  </div>
                  <p className="ml-2 text-sm text-gray-600">
                    Discover unique floral arrangements
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 text-brand-primary mt-0.5">
                    <CheckCircle size={20} />
                  </div>
                  <p className="ml-2 text-sm text-gray-600">
                    Fast, reliable local delivery options
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-auto">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Market Bloom
              </p>
            </div>
          </div>

          {/* Right panel - authentication forms */}
          <div className="md:col-span-2 p-8">
            {/* Tabs for different auth options */}
            <div className="relative flex border-b border-gray-200 mb-8">
              <div
                className={getTabClass(activeTab === "login")}
                onClick={() => setActiveTab("login")}
              >
                <span>Sign In</span>
              </div>
              <div
                className={getTabClass(activeTab === "customer")}
                onClick={() => setActiveTab("customer")}
              >
                <span>Customer Signup</span>
              </div>
              <div
                className={getTabClass(activeTab === "florist")}
                onClick={() => setActiveTab("florist")}
              >
                <span>Florist Signup</span>
              </div>
              <TabIndicator activeTabIndex={tabToIndex(activeTab)} />
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Login Form */}
            {activeTab === "login" && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">Welcome back</h2>
                <form onSubmit={handleLoginSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        type="checkbox"
                        className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                      />
                      <label
                        htmlFor="remember-me"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-brand-primary hover:text-brand-primary/80"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div>
                    <button
                      type="submit"
                      className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#967F81] hover:bg-[#843329] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                      disabled={loginLoading}
                    >
                      {loginLoading ? "Signing in..." : "Sign in"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Customer Signup Form */}
            {activeTab === "customer" && isGoogleMapsLoadedState && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">
                  Create a customer account
                </h2>
                <form onSubmit={handleCustomerSignupSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="customerFullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input id="customerFullName" type="text" placeholder="Your full name" value={customerFormData.fullName} onChange={(e) => setCustomerFormData({ ...customerFormData, fullName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div>
                    <label htmlFor="customerSignupEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input id="customerSignupEmail" type="email" placeholder="your@email.com" value={customerFormData.email} onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div>
                    <label htmlFor="customerSignupPassword" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input id="customerSignupPassword" type="password" placeholder="Min. 6 characters" value={customerFormData.password} onChange={(e) => setCustomerFormData({ ...customerFormData, password: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <h3 className="text-lg font-semibold text-gray-700 pt-4 border-t border-gray-200 mt-6 mb-2">Delivery Address</h3>
                  
                  {/* Google Places Autocomplete at the top */}
                  <div>
                    <label htmlFor="customerAddressSearch" className="block text-sm font-medium text-gray-700 mb-1">
                      Search Your Address <span className="text-xs text-gray-500">(This will auto-fill the fields below)</span>
                    </label>
                    <input
                      id="customerAddressSearch" 
                      type="text"
                      placeholder="Start typing your full address..." 
                      className="w-full px-3 py-2 border-2 border-brand-primary/30 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-[#967F81]/5"
                      ref={(el) => {
                        if (el && isGoogleMapsLoadedState) {
                          onLoadSearchBox(el);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Try typing your full address (e.g., "123 George Street, Sydney NSW") for best results
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="customerStreetNumber" className="block text-sm font-medium text-gray-700 mb-1">Street Number</label>
                    <input id="customerStreetNumber" type="text" placeholder="e.g. 45-49 or 123" value={customerFormData.street_number} onChange={(e) => setCustomerFormData({ ...customerFormData, street_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <div>
                    <label htmlFor="customerStreetName" className="block text-sm font-medium text-gray-700 mb-1">Street Name</label>
                    <input id="customerStreetName" type="text" placeholder="e.g. George Street" value={customerFormData.street_name} onChange={(e) => setCustomerFormData({ ...customerFormData, street_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <div>
                    <label htmlFor="customerUnitNumber" className="block text-sm font-medium text-gray-700 mb-1">Unit Number <span className="text-xs text-gray-500">(Optional)</span></label>
                    <input id="customerUnitNumber" type="text" placeholder="Apartment, suite, unit, etc." value={customerFormData.unit_number} onChange={(e) => setCustomerFormData({ ...customerFormData, unit_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="customerSuburb" className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                      <input id="customerSuburb" type="text" placeholder="e.g. Rosebery" value={customerFormData.suburb} onChange={(e) => setCustomerFormData({ ...customerFormData, suburb: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                    <div>
                      <label htmlFor="customerState" className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                      <input id="customerState" type="text" placeholder="e.g. NSW" value={customerFormData.state} onChange={(e) => setCustomerFormData({ ...customerFormData, state: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="customerPostcode" className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input id="customerPostcode" type="text" placeholder="e.g. 2018" value={customerFormData.postcode} onChange={(e) => setCustomerFormData({ ...customerFormData, postcode: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input id="customerPhone" type="tel" placeholder="Your contact phone number" value={customerFormData.phone} onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div className="flex items-center pt-2">
                    <input id="customerTerms" type="checkbox" checked={customerFormData.agreeToTerms} onChange={(e) => setCustomerFormData({ ...customerFormData, agreeToTerms: e.target.checked })} className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded" required />
                    <label htmlFor="customerTerms" className="ml-2 block text-sm text-gray-700">I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-brand-primary/80">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-brand-primary/80">Privacy Policy</a></label>
                  </div>
                  <div>
                    <button type="submit" className="w-full button-primary-class" disabled={customerSignupLoading || !customerFormData.agreeToTerms}>
                      {customerSignupLoading ? "Creating account..." : "Create account"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Customer Signup Form - Fallback without Google Maps */}
            {activeTab === "customer" && !isGoogleMapsLoadedState && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-semibold mb-6 text-gray-800">
                  Create a customer account
                </h2>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                    Loading Google Maps for address autocomplete...
                  </div>
                </div>
                <form onSubmit={handleCustomerSignupSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="customerFullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input id="customerFullName" type="text" placeholder="Your full name" value={customerFormData.fullName} onChange={(e) => setCustomerFormData({ ...customerFormData, fullName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div>
                    <label htmlFor="customerSignupEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input id="customerSignupEmail" type="email" placeholder="your@email.com" value={customerFormData.email} onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div>
                    <label htmlFor="customerSignupPassword" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input id="customerSignupPassword" type="password" placeholder="Min. 6 characters" value={customerFormData.password} onChange={(e) => setCustomerFormData({ ...customerFormData, password: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <h3 className="text-lg font-semibold text-gray-700 pt-4 border-t border-gray-200 mt-6 mb-2">Delivery Address</h3>
                  
                  <div>
                    <label htmlFor="customerStreetNumber" className="block text-sm font-medium text-gray-700 mb-1">Street Number</label>
                    <input id="customerStreetNumber" type="text" placeholder="e.g. 45-49 or 123" value={customerFormData.street_number} onChange={(e) => setCustomerFormData({ ...customerFormData, street_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <div>
                    <label htmlFor="customerStreetName" className="block text-sm font-medium text-gray-700 mb-1">Street Name</label>
                    <input id="customerStreetName" type="text" placeholder="e.g. George Street" value={customerFormData.street_name} onChange={(e) => setCustomerFormData({ ...customerFormData, street_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>

                  <div>
                    <label htmlFor="customerUnitNumber" className="block text-sm font-medium text-gray-700 mb-1">Unit Number <span className="text-xs text-gray-500">(Optional)</span></label>
                    <input id="customerUnitNumber" type="text" placeholder="Apartment, suite, unit, etc." value={customerFormData.unit_number} onChange={(e) => setCustomerFormData({ ...customerFormData, unit_number: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="customerSuburb" className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                      <input id="customerSuburb" type="text" placeholder="e.g. Rosebery" value={customerFormData.suburb} onChange={(e) => setCustomerFormData({ ...customerFormData, suburb: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                    <div>
                      <label htmlFor="customerState" className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
                      <input id="customerState" type="text" placeholder="e.g. NSW" value={customerFormData.state} onChange={(e) => setCustomerFormData({ ...customerFormData, state: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="customerPostcode" className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                      <input id="customerPostcode" type="text" placeholder="e.g. 2018" value={customerFormData.postcode} onChange={(e) => setCustomerFormData({ ...customerFormData, postcode: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input id="customerPhone" type="tel" placeholder="Your contact phone number" value={customerFormData.phone} onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" required />
                  </div>
                  <div className="flex items-center pt-2">
                    <input id="customerTerms" type="checkbox" checked={customerFormData.agreeToTerms} onChange={(e) => setCustomerFormData({ ...customerFormData, agreeToTerms: e.target.checked })} className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded" required />
                    <label htmlFor="customerTerms" className="ml-2 block text-sm text-gray-700">I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-brand-primary/80">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:text-brand-primary/80">Privacy Policy</a></label>
                  </div>
                  <div>
                    <button type="submit" className="w-full button-primary-class" disabled={customerSignupLoading || !customerFormData.agreeToTerms}>
                      {customerSignupLoading ? "Creating account..." : "Create account"}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Florist Signup Form */}
            {activeTab === "florist" && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-semibold mb-2 text-gray-800">
                  Join as a florist
                </h2>
                <p className="text-gray-600 mb-6">
                  Join hundreds of florists across Australia who are reaching more customers
                  in their local area.
                </p>
                <form onSubmit={handleFloristSignupSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="floristFullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Full Name
                    </label>
                    <input
                      id="floristFullName"
                      type="text"
                      value={floristFormData.fullName}
                      onChange={(e) => setFloristFormData({ ...floristFormData, fullName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="storeName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Store Name
                    </label>
                    <input
                      id="storeName"
                      type="text"
                      value={floristFormData.storeName}
                      onChange={(e) => setFloristFormData({ ...floristFormData, storeName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="floristEmail"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="floristEmail"
                      type="email"
                      value={floristFormData.email}
                      onChange={(e) => setFloristFormData({ ...floristFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="floristPassword"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Password
                    </label>
                    <input
                      id="floristPassword"
                      type="password"
                      value={floristFormData.password}
                      onChange={(e) => setFloristFormData({ ...floristFormData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={floristFormData.phone}
                      onChange={(e) => setFloristFormData({ ...floristFormData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    />
                  </div>
                  <div className="bg-brand-muted p-4 rounded-md border border-brand-primary/20">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Note:</span> After registering, you'll be able to create your store profile and set up all your products right away. Once your store is ready, our team will review and approve it before making it visible to customers.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="floristTerms"
                      type="checkbox"
                      checked={floristFormData.agreeToTerms}
                      onChange={(e) => setFloristFormData({ ...floristFormData, agreeToTerms: e.target.checked })}
                      className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                      required
                    />
                    <label
                      htmlFor="floristTerms"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      I agree to the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:text-brand-primary/80"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:text-brand-primary/80"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                  <div>
                    <button
                      type="submit"
                      className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#967F81] hover:bg-[#843329] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                      disabled={floristSignupLoading || !floristFormData.agreeToTerms}
                    >
                      {floristSignupLoading ? "Creating account..." : "Apply to join"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth; 